import React, { useLayoutEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { ElementType } from '../../types';
import { getFactory3DStyle } from '../../utils/factory3dStyles';
import { InstanceBatchResult } from '../../workers/scene3dCompute';
import { use3dInstanceBatches } from '../../hooks/use3dInstanceBatches';
import { LayoutElement } from '../../types';

const tempObject = new THREE.Object3D();
const tempColor = new THREE.Color();

function materialPropsForType(type: ElementType) {
  const style = getFactory3DStyle(type);
  return {
    metalness: style.metalness,
    roughness: style.roughness,
    emissive: style.emissive ?? '#000000',
    emissiveIntensity: style.emissiveIntensity ?? 0,
    opacity: style.opacity ?? 1,
    transparent: (style.opacity ?? 1) < 1,
  };
}

const FLOOR_EQUIPMENT_TYPES: ReadonlySet<ElementType> = new Set([
  'stb',
  'ohb',
  'lifter',
  'oht-vehicle',
]);

const InstancedBatchMesh: React.FC<{ batch: InstanceBatchResult }> = React.memo(({ batch }) => {
  const meshRef = useRef<THREE.InstancedMesh>(null);
  const geometry = useMemo(() => new THREE.BoxGeometry(1, 1, 1), []);
  const matProps = useMemo(() => materialPropsForType(batch.elementType), [batch.elementType]);
  const castsShadow = FLOOR_EQUIPMENT_TYPES.has(batch.elementType);

  useLayoutEffect(() => {
    const mesh = meshRef.current;
    if (!mesh) return;

    for (let i = 0; i < batch.count; i++) {
      tempObject.matrix.fromArray(batch.matrices, i * 16);
      mesh.setMatrixAt(i, tempObject.matrix);

      tempColor.setRGB(batch.colors[i * 3]!, batch.colors[i * 3 + 1]!, batch.colors[i * 3 + 2]!);
      mesh.setColorAt(i, tempColor);
    }

    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  }, [batch]);

  return (
    <instancedMesh
      ref={meshRef}
      args={[geometry, undefined, batch.count]}
      frustumCulled
      castShadow={castsShadow}
      receiveShadow={castsShadow}
    >
      <meshStandardMaterial vertexColors {...matProps} />
    </instancedMesh>
  );
});

InstancedBatchMesh.displayName = 'InstancedBatchMesh';

export type InstancedElements3DProps = {
  elements: Record<string, LayoutElement>;
  elementOrder: string[];
  hiddenLayerIds?: Set<string>;
  /** Pre-built batches from worker (skips second worker pass). */
  precomputedBatches?: InstanceBatchResult[] | null;
  onComputingChange?: (computing: boolean) => void;
};

const InstancedElements3D: React.FC<InstancedElements3DProps> = React.memo(
  ({ elements, elementOrder, hiddenLayerIds, precomputedBatches, onComputingChange }) => {
    const hidden = hiddenLayerIds ?? new Set<string>();

    const { batches: computedBatches, computing } = use3dInstanceBatches(
      elements,
      elementOrder,
      hidden,
      !precomputedBatches?.length
    );

    const batches = precomputedBatches?.length ? precomputedBatches : computedBatches;

    React.useEffect(() => {
      onComputingChange?.(precomputedBatches?.length ? false : computing);
    }, [computing, onComputingChange, precomputedBatches]);

    return (
      <>
        {batches.map((batch) => (
          <InstancedBatchMesh key={batch.key} batch={batch} />
        ))}
      </>
    );
  }
);

InstancedElements3D.displayName = 'InstancedElements3D';

export default InstancedElements3D;
